import React from "react"
import { QualityBadge } from "./DashboardComponents"
import { DefinitionButton } from "./DefinitionExperience"
import {
  formatBusinessDate,
  formatMoney,
  percentageLabel,
} from "./formatters"

const componentNote = key => ({
  eventCollections: "All Event collections included in management Net Revenue",
  eventServiceFee: "Sportya earned from events",
  mixedEventCollections: "Full legacy charges containing Sportya and club amounts",
  premiumRevenue: "Sportya earned from paid Premium",
  bookingPlatformFee: "Sportya earned from Court Bookings",
  clubPayable: "Club pass-through liability",
  unallocatedCollection: "Ownership unresolved",
}[key] || "Reported component")

const RevenueComposition = ({ breakdown, ariaLabel }) => (
  <section className="analytics-revenue-composition" role="region" aria-label={ariaLabel}>
    <div className="analytics-revenue-row is-heading" aria-hidden="true">
      <span>Component</span><span>Current</span><span>Comparison</span><span>Change</span>
    </div>
    {breakdown.rows.map(row => (
      <article className={`analytics-revenue-row analytics-revenue-${row.key}`} key={row.key}>
        <div>
          <strong>{row.label}</strong>
          <small>{componentNote(row.key)}</small>
        </div>
        <span><small>Current</small>{formatMoney(row.current)}</span>
        <span><small>Comparison</small>{formatMoney(row.comparison)}</span>
        <span className={(row.delta.absolute || 0) >= 0 ? "is-positive" : "is-negative"}>
          <small>Change</small>{percentageLabel(row.delta.percentage)}
        </span>
      </article>
    ))}
  </section>
)

const metricValue = metric => metric.unit === "RON"
  ? formatMoney(metric.current.value)
  : metric.current.value === null || metric.current.value === undefined
    ? "Unavailable"
    : Number(metric.current.value).toLocaleString("en-GB")

const comparisonValue = metric => metric.unit === "RON"
  ? formatMoney(metric.comparison.value)
  : metric.comparison.value === null || metric.comparison.value === undefined
    ? "Unavailable"
    : Number(metric.comparison.value).toLocaleString("en-GB")

const lifecycleSummary = (label, lifecycle = {}) => (
  `${label}: New ${lifecycle.new || 0} · Renewals ${lifecycle.renewal || 0} · Reactivations ${lifecycle.reactivation || 0}`
  + ((lifecycle.unclassified || 0) > 0 ? ` · Unclassified legacy ${lifecycle.unclassified}` : "")
)

const PremiumSection = ({ premium }) => {
  if (!premium?.metrics?.length) return null
  return (
    <section className="analytics-premium-section" aria-labelledby="premium-revenue-title">
      <div className="analytics-section-heading">
        <div>
          <p className="analytics-eyebrow">Recurring revenue driver</p>
          <h2 id="premium-revenue-title">Premium</h2>
          <p>Period-end account activity, paid lifecycle and non-revenue admin grants stay separate.</p>
        </div>
      </div>
      <div className="analytics-premium-grid">
        {premium.metrics.map(metric => (
          <article key={metric.key} className={`analytics-premium-${metric.key}`}>
            <div><span>{metric.label}</span><QualityBadge quality={metric.current.quality} /></div>
            <strong>{metricValue(metric)}</strong>
            <p>
              <b className={(metric.delta.absolute || 0) >= 0 ? "is-positive" : "is-negative"}>
                {percentageLabel(metric.delta.percentage)}
              </b>
              <span> vs {comparisonValue(metric)}</span>
            </p>
            {metric.key === "paidPremiumSales" && (
              <>
                <small>{lifecycleSummary("Current", premium.current.lifecycle)}</small>
                <small>{lifecycleSummary("Comparison", premium.comparison.lifecycle)}</small>
              </>
            )}
            {metric.key === "adminGrantCount" && <small>Context only · 0 RON revenue</small>}
            <small>{metric.definition?.summary}</small>
            <DefinitionButton metric={metric} />
          </article>
        ))}
      </div>
    </section>
  )
}

const bookingValue = metric => {
  const value = metric.current.value
  if (metric.unit === "RON") return formatMoney(value)
  if (value === null || value === undefined) return "Unavailable"
  return Number(value).toLocaleString("en-GB", { maximumFractionDigits: 2 })
}

const bookingComparisonValue = metric => {
  const value = metric.comparison.value
  if (metric.unit === "RON") return formatMoney(value)
  if (value === null || value === undefined) return "Unavailable"
  return Number(value).toLocaleString("en-GB", { maximumFractionDigits: 2 })
}

const contextValue = metric => {
  const value = metric?.current?.value
  if (value === null || value === undefined) return "Unavailable"
  if (metric.unit === "percent") return `${Number(value).toFixed(1)}%`
  return Number(value).toLocaleString("en-GB", { maximumFractionDigits: 2 })
}

const CourtBookingsSection = ({ booking }) => {
  if (!booking?.metrics?.length) return null
  const drivers = Object.values(booking.drivers || {})
  const guardrails = Object.values(booking.guardrails || {})
  return (
    <section className="analytics-booking-section" aria-labelledby="booking-revenue-title">
      <div className="analytics-section-heading">
        <div>
          <p className="analytics-eyebrow">Demand and earned revenue</p>
          <h2 id="booking-revenue-title">Court Bookings</h2>
          <p>Elapsed confirmed demand and the resolved booking platform fee, aligned to the selected periods.</p>
        </div>
      </div>
      <div className="analytics-booking-grid" aria-label="Court Bookings measures">
        {booking.metrics.map(metric => (
          <article key={metric.key} className={`analytics-booking-${metric.key}`}>
            <div>
              <span>{metric.label}</span>
              <QualityBadge quality={metric.current.quality} />
            </div>
            <small className="analytics-booking-current-label">Current</small>
            <strong>{bookingValue(metric)}</strong>
            <p>
              <span>{`Comparison ${bookingComparisonValue(metric)}`}</span>
              <b className={(metric.delta.absolute || 0) >= 0 ? "is-positive" : "is-negative"}>
                {`${percentageLabel(metric.delta.percentage)} change`}
              </b>
            </p>
            <small>{metric.definition?.summary}</small>
            <DefinitionButton metric={metric} />
          </article>
        ))}
      </div>
      <div className="analytics-booking-context">
        <div aria-label="Court Bookings demand drivers">
          <h3>Demand context</h3>
          {drivers.map(metric => (
            <article key={metric.key}>
              <span>{metric.label}</span>
              <strong>{contextValue(metric)}</strong>
              <small>Comparison {metric.comparison.value === null ? "Unavailable" : Number(metric.comparison.value).toLocaleString("en-GB", { maximumFractionDigits: 2 })}</small>
            </article>
          ))}
        </div>
        <div aria-label="Court Bookings guardrails">
          <h3>Guardrails</h3>
          {guardrails.map(metric => (
            <article key={metric.key}>
              <span>{metric.label}</span>
              <strong>{contextValue(metric)}</strong>
              <small>Comparison {metric.comparison.value === null ? "Unavailable" : `${Number(metric.comparison.value).toFixed(1)}%`}</small>
            </article>
          ))}
        </div>
      </div>
      <p className="analytics-booking-note">
        “Elapsed confirmed” means the booked interval has ended and the booking is still confirmed; it does not claim attendance, completion or no-show status.
      </p>
    </section>
  )
}

const Reconciliation = ({ label, range }) => (
  <article>
    <span>{label}</span>
    <strong>{formatMoney(range.reconciliation.expected)} GPV</strong>
    <small>
      {formatMoney(range.reconciliation.actual)} allocated · {" "}
      <b>{formatMoney(range.reconciliation.discrepancy === null ? null : Math.abs(range.reconciliation.discrepancy))} discrepancy</b>
    </small>
  </article>
)

const ExcludedFacts = ({ label, facts }) => (
  <div>
    <h4>{label}</h4>
    {facts.length ? facts.map((fact, index) => (
      <p key={`${label}-${fact.currency}-${fact.businessDate}-${index}`}>
        {fact.currency} {Number(fact.amount).toFixed(2)} · Business Date {formatBusinessDate(fact.businessDate)} · {fact.reason}
      </p>
    )) : <p>No {label.toLowerCase()} facts were excluded for missing FX evidence.</p>}
  </div>
)

const Evidence = ({ current, comparison }) => {
  const fxEvidence = [...(current.fxEvidence || []), ...(comparison.fxEvidence || [])]
  return (
    <section className="analytics-revenue-evidence" aria-labelledby="revenue-evidence-title">
      <div className="analytics-section-heading">
        <div>
          <p className="analytics-eyebrow">Audit trail</p>
          <h2 id="revenue-evidence-title">Coverage and RON evidence</h2>
        </div>
      </div>
      <div className="analytics-quality-callouts">
        <div><QualityBadge quality={current.quality} /><p><strong>Current:</strong> {current.quality.reason}</p></div>
        <div><QualityBadge quality={comparison.quality} /><p><strong>Comparison:</strong> {comparison.quality.reason}</p></div>
      </div>
      <div className="analytics-evidence-groups">
        <div>
          <h3>Historical FX used</h3>
          {fxEvidence.length ? fxEvidence.map((evidence, index) => (
            <p key={`${evidence.currency}-${evidence.businessDate}-${index}`}>
              {evidence.currency} · {evidence.rate} RON · rate date {formatBusinessDate(evidence.rateDate)} · Business Date {formatBusinessDate(evidence.businessDate)} · {evidence.source}
            </p>
          )) : <p>No historical FX evidence is available for this selection.</p>}
        </div>
        <div>
          <h3>Excluded from RON totals</h3>
          <ExcludedFacts label="Current period" facts={current.excludedFacts || []} />
          <ExcludedFacts label="Comparison period" facts={comparison.excludedFacts || []} />
        </div>
      </div>
    </section>
  )
}

const RevenueView = ({ snapshot }) => {
  const netRevenue = snapshot.kpis?.find(({ key }) => key === "netRevenue")
  const breakdown = snapshot.breakdowns?.eventRevenueComposition
  const netBreakdown = snapshot.breakdowns?.netRevenueComposition
  const current = snapshot.revenue?.current
  const comparison = snapshot.revenue?.comparison
  const hasMixedEventCollections = current?.hasMixedEventCollections
    || comparison?.hasMixedEventCollections
    || [current?.mixedEventCollections, comparison?.mixedEventCollections]
      .some(value => Number.isFinite(value) && value !== 0)
  if (!netRevenue || !breakdown || !current || !comparison) {
    return <div className="analytics-state">Revenue is unavailable for this selection.</div>
  }
  return (
    <section className="analytics-revenue-view" aria-labelledby="revenue-view-title">
      <div className="analytics-view-heading">
        <div>
          <p className="analytics-eyebrow">Revenue perspective</p>
          <h2 id="revenue-view-title">Revenue</h2>
          <p>Management collections, explicit club pass-through and unresolved amounts remain visibly separate.</p>
        </div>
      </div>
      <article className="analytics-revenue-hero">
        <div>
          <span>Net Revenue</span>
          <strong>{formatMoney(netRevenue.current.value)}</strong>
          <p>{netRevenue.definition.summary}</p>
          <DefinitionButton metric={netRevenue} />
        </div>
        <div>
          <span>Comparison</span>
          <strong>{formatMoney(netRevenue.comparison.value)}</strong>
          <b className={(netRevenue.delta.absolute || 0) >= 0 ? "is-positive" : "is-negative"}>
            {percentageLabel(netRevenue.delta.percentage)}
          </b>
        </div>
        <QualityBadge quality={netRevenue.current.quality} />
      </article>
      {netBreakdown && (
        <section className="analytics-revenue-sources" aria-labelledby="net-revenue-sources-title">
          <div className="analytics-section-heading">
            <div>
              <p className="analytics-eyebrow">Management sources</p>
              <h2 id="net-revenue-sources-title">Net Revenue sources</h2>
            </div>
          </div>
          <RevenueComposition breakdown={netBreakdown} ariaLabel="Net revenue composition" />
        </section>
      )}
      <PremiumSection premium={snapshot.premium} />
      <CourtBookingsSection booking={snapshot.booking} />
      <section className="analytics-event-ownership" aria-labelledby="event-ownership-title">
        <div className="analytics-section-heading">
          <div>
            <p className="analytics-eyebrow">Event economics</p>
            <h2 id="event-ownership-title">Event collection ownership</h2>
          </div>
        </div>
        {hasMixedEventCollections && (
          <aside className="analytics-definition-note analytics-mixed-collection-note">
            <strong>Mixed Event Collections are included</strong>
            <p>
              {formatMoney(current.mixedEventCollections)} current and {formatMoney(comparison.mixedEventCollections)} comparison full legacy charges are included in management Net Revenue. These amounts contain both the Sportya Service Fee and a club amount that is, or will be, reconciled separately; no historical split is inferred.
            </p>
          </aside>
        )}
        <RevenueComposition breakdown={breakdown} ariaLabel="Event revenue composition" />
      </section>
      <section className="analytics-revenue-reconciliation" aria-label="Revenue GPV reconciliation">
        <Reconciliation label="Current reconciliation" range={current} />
        <Reconciliation label="Comparison reconciliation" range={comparison} />
        <article>
          <span>Outside reported GPV</span>
          <strong>{formatMoney(current.clubCollectedDirect)} direct club collection</strong>
          <small>Comparison {formatMoney(comparison.clubCollectedDirect)} · never included in Sportya Net Revenue</small>
        </article>
      </section>
      <aside className="analytics-definition-note analytics-revenue-definition">
        <strong>Objective ownership rule</strong>
        <p>Explicit Club Payable is money owed to clubs and stays outside management Net Revenue. For Mixed Event Collections, full legacy charges are included in management Net Revenue and disclosed separately because the exact Sportya-versus-club split is not stored. Direct club collection stays outside reported GPV. Unallocated Collection remains visible until transaction evidence establishes ownership. Admin Premium grants contribute zero revenue.</p>
        <p>{netRevenue.definition.scopeNote}</p>
      </aside>
      <Evidence current={current} comparison={comparison} />
    </section>
  )
}

export default RevenueView
